import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import emailjs from '@emailjs/browser';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { MailIcon, PhoneIcon, MapPinIcon, ClockIcon, ArrowRightIcon, ChevronRightIcon } from 'lucide-react';
// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
// Header slideshow images
const headerImages = ['https://uploadthingy.s3.us-west-1.amazonaws.com/qaftZRiHhTuL8i96x78g8e/bernd-dittrich-0vAAngcguI0-unsplash.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/xkcnd2wwVAY9t9Dw3pT4pE/grant-durr-aJEWd6QDfSE-unsplash.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/sFzhtxaCFxDmk6Wq3Caatn/kurt-cotoaga-oaRNa2et8Vg-unsplash.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/u4QHFdWKSmdiaJGSPoptZN/thisisengineering-yhCHx8Mc-Kc-unsplash.jpg'];
export function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    consent: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sections = useRef([]);
  const observerRef = useRef(null);
  const formRef = useRef(null);
  // Office locations with coordinates
  const officeLocations = [{
    name: 'Head Office',
    address: '1 Liang Seah Street, #02-02, Singapore 189022',
    position: [1.297, 103.8536],
    phone: '+65 8126 6622',
    fax: '+65 6234 3152',
    email: 'contact@cosmoimageaviation.co'
  }, {
    name: 'Airport Warehouse',
    address: '115 Airport Cargo Road, #01-01 & #01-06, Cargo Agents Building C, Singapore 819466',
    position: [1.3644, 103.9915],
    phone: '+65 8126 6622 / +65 9663 0738',
    fax: '+65 6543 1130'
  }];
  // Slideshow effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % headerImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  // Check system preference for dark mode
  useEffect(() => {
    const darkModePreference = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(darkModePreference.matches);
    const handleChange = e => setIsDarkMode(e.matches);
    darkModePreference.addEventListener('change', handleChange);
    return () => darkModePreference.removeEventListener('change', handleChange);
  }, []);
  // Setup intersection observer for active section highlighting
  useEffect(() => {
    observerRef.current = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, {
      threshold: 0.3
    });
    const sectionElements = document.querySelectorAll('section[id]');
    sections.current = Array.from(sectionElements);
    sections.current.forEach(section => {
      observerRef.current.observe(section);
    });
    return () => {
      sections.current.forEach(section => {
        observerRef.current.unobserve(section);
      });
    };
  }, []);
  // Handle form input changes
  const handleInputChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    if (!formData.message.trim()) errors.message = 'Message is required';
    if (!formData.consent) errors.consent = 'You must consent to contact';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  // Handle form submission with EmailJS
  const handleSubmit = async e => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        const serviceId = 'YOUR_EMAILJS_SERVICE_ID';
        const templateId = 'YOUR_EMAILJS_TEMPLATE_ID';
        const publicKey = 'YOUR_EMAILJS_PUBLIC_KEY';
        const templateParams = {
          from_name: formData.name,
          from_email: formData.email,
          company: formData.company || 'Not provided',
          message: formData.message,
          to_email: 'contact@cosmoimageaviation.co'
        };
        await emailjs.send(serviceId, templateId, templateParams, publicKey);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
        setFormData({
          name: '',
          email: '',
          company: '',
          message: '',
          consent: false
        });
      } catch (error) {
        console.error('Failed to send email:', error);
        alert('Failed to send email. Please try again later or contact us directly.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  // Smooth scroll to section
  const scrollToSection = sectionId => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({
        behavior: 'smooth'
      });
    }
    setIsMenuOpen(false);
  };
  return <div className={`min-h-screen w-full ${isDarkMode ? 'dark' : ''}`} data-id="element-0">
      <div className="text-text dark:text-gray-200 dark:bg-dark font-sans" data-id="element-1">
        <Header activeSection={activeSection} onNavigate={scrollToSection} data-id="element-2" />

        {/* Hero Section with Slideshow - Full viewport height */}
        <section id="home" className="relative min-h-screen flex items-center overflow-hidden isolate" data-id="element-3">
          {/* Slideshow backgrounds */}
          {headerImages.map((img, index) => <div key={index} className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out bg-cover bg-center z-0 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`} style={{
          backgroundImage: `url(${img})`
        }} data-id="element-4"></div>)}

          {/* Cinematic Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-navy/90 via-navy/60 to-transparent z-[1]" data-id="element-5"></div>

          <div className="container mx-auto max-w-[var(--maxw)] px-4 sm:px-6 lg:px-8 relative z-10 pt-24 sm:pt-20" data-id="element-6">
            <div className="max-w-4xl" data-id="element-7">
              <div className="inline-block border-l-4 border-teal pl-4 mb-4 sm:mb-6" data-id="element-8">
                <span className="text-teal font-bold tracking-widest uppercase text-xs sm:text-sm" data-id="element-9">
                  Global Logistics Partner
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-4 sm:mb-6 md:mb-8 text-white leading-tight" data-id="element-10">
                Delivering Beyond Borders, <br className="hidden sm:block" data-id="element-11" />
                <span className="text-teal" data-id="element-12">Managing with Excellence.</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 md:mb-12 max-w-2xl text-gray-200 font-light leading-relaxed" data-id="element-13">
                Singapore-based logistics partner providing freight transport,
                management consultancy services, and high-tech aviation
                technology solutions with global reach.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4" data-id="element-14">
                <button onClick={() => scrollToSection('services')} className="bg-teal hover:bg-teal/90 text-white font-bold py-3 sm:py-4 px-6 sm:px-10 rounded-none transition-all flex items-center justify-center gap-2 group text-sm sm:text-base" data-id="element-15">
                  Our Services
                  <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" data-id="element-16" />
                </button>
                <button onClick={() => scrollToSection('contact')} className="bg-white hover:bg-gray-100 text-navy font-bold py-3 sm:py-4 px-6 sm:px-10 rounded-none transition-all text-sm sm:text-base" data-id="element-17">
                  Contact Us
                </button>
              </div>
            </div>
          </div>

          {/* Slideshow indicators */}
          <div className="absolute bottom-6 sm:bottom-10 left-0 right-0 flex justify-center gap-2 sm:gap-3 z-10" data-id="element-18">
            {headerImages.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`h-1 transition-all duration-300 ${index === currentSlide ? 'bg-teal w-12' : 'bg-white/50 w-8 hover:bg-white'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-19"></button>)}
          </div>
        </section>

        {/* About Us Section - Clean Editorial Layout */}
        <section id="about" className="py-16 sm:py-20 lg:py-24 bg-white dark:bg-dark" data-id="element-20">
          <div className="container mx-auto max-w-[var(--maxw)] px-4" data-id="element-21">
            <div className="flex flex-col md:flex-row gap-8 sm:gap-12 lg:gap-16" data-id="element-22">
              <div className="md:w-1/3" data-id="element-23">
                <span className="text-teal font-bold tracking-widest uppercase text-xs sm:text-sm block mb-3 sm:mb-4" data-id="element-24">
                  ABOUT US
                </span>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy dark:text-white mb-6 sm:mb-8 leading-tight" data-id="element-25">
                  Revolutionizing global logistics.
                </h2>
                <div className="h-1 w-20 bg-teal mb-6 sm:mb-8" data-id="element-26"></div>
              </div>

              <div className="md:w-2/3 space-y-8 sm:space-y-12" data-id="element-27">
                <div className="bg-gray-50 dark:bg-gray-800/50 p-6 sm:p-8 lg:p-10 border-l-4 border-navy" data-id="element-28">
                  <h3 className="text-xl sm:text-2xl font-bold text-navy dark:text-white mb-3 sm:mb-4" data-id="element-29">
                    Vision
                  </h3>
                  <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300 leading-relaxed italic" data-id="element-30">
                    "To revolutionize global logistics and aviation technology,
                    becoming the world's most trusted partner for innovative,
                    efficient, and sustainable transportation solutions."
                  </p>
                </div>

                <div data-id="element-31">
                  <h3 className="text-xl sm:text-2xl font-bold text-navy dark:text-white mb-4 sm:mb-6" data-id="element-32">
                    Mission
                  </h3>
                  <ul className="space-y-3 sm:space-y-4 text-base sm:text-lg text-gray-600 dark:text-gray-300" data-id="element-33">
                    {['Deliver integrated, transparent, and cost-efficient logistics solutions.', 'Provide cutting-edge aviation technology and security solutions to enhance operational efficiency and safety.', 'Uphold client satisfaction through professionalism and sustainability.', 'Expand strategic networks to strengthen global supply chains.', 'Drive innovation in aviation IT systems to meet the evolving needs of the industry.'].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-34">
                        <span className="text-teal mt-1.5" data-id="element-35">
                          <ChevronRightIcon className="w-5 h-5" data-id="element-36" />
                        </span>
                        <span data-id="element-37">{item}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section - Grid with Accent Borders */}
        <section id="services" className="py-16 sm:py-20 lg:py-24 bg-gray-50 dark:bg-gray-900" data-id="element-38">
          <div className="container mx-auto max-w-[var(--maxw)] px-4" data-id="element-39">
            <div className="mb-10 sm:mb-16" data-id="element-40">
              <span className="text-teal font-bold tracking-widest uppercase text-xs sm:text-sm block mb-3 sm:mb-4" data-id="element-41">
                WHAT WE DO
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy dark:text-white mb-4 sm:mb-6" data-id="element-42">
                Our Services
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 dark:text-gray-400 max-w-3xl" data-id="element-43">
                Comprehensive solutions tailored to your logistics and aviation
                needs.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8" data-id="element-44">
              {[{
              title: 'Air Cargo & Charter Solutions',
              desc: 'Specialized air freight services for time-sensitive and high-value shipments with global charter capabilities.'
            }, {
              title: 'Consolidation & Distribution',
              desc: 'Efficient cargo consolidation services to optimize shipping costs and streamline distribution networks.'
            }, {
              title: 'Customs & Compliance',
              desc: 'Expert guidance on customs regulations, documentation, and compliance to ensure smooth cross-border movements.'
            }, {
              title: 'Multimodal Transport',
              desc: 'Integrated transport solutions combining air, sea, and land to optimize routing and delivery timelines.'
            }, {
              title: 'Aviation IT Solutions',
              desc: 'Specialized IT services for the aviation industry including flight management systems, maintenance tracking software, and custom aviation applications.'
            }, {
              title: 'Aviation Security Technology',
              desc: 'Comprehensive security solutions for aviation including advanced screening systems, cybersecurity for aviation networks, and secure communications infrastructure for airports and airlines.'
            }, {
              title: 'Supply Chain Advisory',
              desc: 'Strategic consulting to optimize supply chain operations, reduce costs, and improve service levels.'
            }, {
              title: 'Process & Cost Optimization',
              desc: 'Detailed analysis and improvement of logistics processes to enhance efficiency and reduce operational costs.'
            }, {
              title: 'System Integration & Reporting',
              desc: 'Implementation of integrated logistics systems and customized reporting solutions for better visibility.'
            }, {
              title: 'Market Expansion for Freight Forwarders',
              desc: 'Strategic guidance for freight forwarders looking to expand into new markets or service areas.'
            }].map((service, index) => <article key={index} className="group bg-white dark:bg-gray-800 p-6 sm:p-8 shadow-sm hover:shadow-xl transition-all duration-300 border-l-4 border-transparent hover:border-teal relative h-full flex flex-col" itemScope itemType="https://schema.org/Service" data-id="element-45">
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-navy dark:text-white mb-3 sm:mb-4 group-hover:text-teal transition-colors" itemProp="name" data-id="element-46">
                    {service.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 leading-relaxed mb-6 sm:mb-8 flex-grow" itemProp="description" data-id="element-47">
                    {service.desc}
                  </p>
                  <div className="mt-auto" data-id="element-48">
                    <span className="inline-flex items-center text-navy dark:text-white font-bold group-hover:translate-x-2 transition-transform" data-id="element-49">
                      Learn more{' '}
                      <ArrowRightIcon className="w-4 h-4 ml-2 text-teal" data-id="element-50" />
                    </span>
                  </div>
                </article>)}
            </div>
          </div>
        </section>

        {/* Leadership Section - Spacious & Professional */}
        <section id="leadership" className="py-16 sm:py-20 lg:py-24 bg-white dark:bg-dark" data-id="element-51">
          <div className="container mx-auto max-w-[var(--maxw)] px-4" data-id="element-52">
            <div className="text-center mb-12 sm:mb-16 lg:mb-20" data-id="element-53">
              <span className="text-teal font-bold tracking-widest uppercase text-xs sm:text-sm block mb-3 sm:mb-4" data-id="element-54">
                OUR TEAM
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy dark:text-white mb-4 sm:mb-6" data-id="element-55">
                Leadership
              </h2>
              <div className="h-1 w-20 bg-teal mx-auto" data-id="element-56"></div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-12" data-id="element-57">
              {[{
              initials: 'LKP',
              name: 'Lee Kok Ping',
              role: 'Managing Director / Director'
            }, {
              initials: 'LMH',
              name: 'Lee Ming Han',
              role: 'Director'
            }, {
              initials: 'TYC',
              name: 'Tan Yong Chuan (Ricky)',
              role: 'Director'
            }, {
              initials: 'TTK',
              name: 'Thorng Tohkingkeo',
              role: 'Company Secretary'
            }].map((leader, index) => <div key={index} className="text-center group" itemScope itemType="https://schema.org/Person" data-id="element-58">
                  <div className="w-20 h-20 sm:w-28 sm:h-28 lg:w-32 lg:h-32 mx-auto bg-navy text-white rounded-full flex items-center justify-center text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 group-hover:bg-teal transition-colors shadow-lg" data-id="element-59">
                    {leader.initials}
                  </div>
                  <h3 className="text-base sm:text-lg lg:text-2xl font-bold text-navy dark:text-white mb-1 sm:mb-2" itemProp="name" data-id="element-60">
                    {leader.name}
                  </h3>
                  <p className="text-teal font-medium uppercase tracking-wide text-[10px] sm:text-xs lg:text-sm" data-id="element-61">
                    {leader.role}
                  </p>
                </div>)}
            </div>
          </div>
        </section>

        {/* Locations Section - Cinematic Overlay */}
        <section id="locations" className="py-16 sm:py-20 lg:py-24 relative isolate" data-id="element-62">
          <div className="absolute inset-0 bg-cover bg-center bg-no-repeat z-0" style={{
          backgroundImage: "url('https://uploadthingy.s3.us-west-1.amazonaws.com/wdsQDFpi8nkS5zhmkqTjJh/swapnil-bapat-sJ7pYyJFyuA-unsplash.jpg')"
        }} data-id="element-63"></div>
          <div className="absolute inset-0 bg-navy/80 z-[1]" data-id="element-64"></div>

          <div className="container mx-auto max-w-[var(--maxw)] px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-65">
            <div className="flex flex-col lg:flex-row gap-8 sm:gap-12 lg:gap-16 items-start" data-id="element-66">
              <div className="lg:w-1/3 text-white w-full" data-id="element-67">
                <span className="text-teal font-bold tracking-widest uppercase text-xs sm:text-sm block mb-3 sm:mb-4" data-id="element-68">
                  GLOBAL PRESENCE
                </span>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 sm:mb-8" data-id="element-69">
                  Locations
                </h2>
                <div className="space-y-4 sm:space-y-6 lg:space-y-8" data-id="element-70">
                  <div className="bg-white/10 backdrop-blur-md p-5 sm:p-6 lg:p-8 border-l-4 border-teal" data-id="element-71">
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-4" data-id="element-72">
                      Head Office
                    </h3>
                    <address className="not-italic text-gray-200 leading-relaxed text-sm sm:text-base" data-id="element-73">
                      1 Liang Seah Street, #02-02
                      <br data-id="element-74" />
                      Singapore 189022
                      <br data-id="element-75" />
                      <br data-id="element-76" />
                      <div className="space-y-2" data-id="element-77">
                        <p data-id="element-78">
                          <strong className="text-white" data-id="element-79">Tel:</strong> +65 8126
                          6622
                        </p>
                        <p data-id="element-80">
                          <strong className="text-white" data-id="element-81">Fax:</strong> +65 6234
                          3152
                        </p>
                        <p data-id="element-82">
                          <strong className="text-white" data-id="element-83">Email:</strong>{' '}
                          contact@cosmoimageaviation.co
                        </p>
                      </div>
                    </address>
                  </div>

                  <div className="bg-white/10 backdrop-blur-md p-5 sm:p-6 lg:p-8 border-l-4 border-teal" data-id="element-84">
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-4" data-id="element-85">
                      Airport Warehouse
                    </h3>
                    <address className="not-italic text-gray-200 leading-relaxed text-sm sm:text-base" data-id="element-86">
                      115 Airport Cargo Road, #01-01 & #01-06
                      <br data-id="element-87" />
                      Cargo Agents Building C<br data-id="element-88" />
                      Singapore 819466
                      <br data-id="element-89" />
                      <br data-id="element-90" />
                      <div className="space-y-2" data-id="element-91">
                        <p data-id="element-92">
                          <strong className="text-white" data-id="element-93">Tel:</strong> +65 8126
                          6622 / +65 9663 0738
                        </p>
                        <p data-id="element-94">
                          <strong className="text-white" data-id="element-95">Fax:</strong> +65 6543
                          1130
                        </p>
                      </div>
                    </address>
                  </div>
                </div>
              </div>

              <div className="lg:w-2/3 w-full" data-id="element-96">
                <div className="bg-white p-1 sm:p-2 shadow-2xl h-[300px] sm:h-[400px] lg:h-[600px] w-full" data-id="element-97">
                  <MapContainer center={[1.297, 103.8536]} zoom={15} style={{
                  height: '100%',
                  width: '100%'
                }} scrollWheelZoom={false} data-id="element-98">
                    <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-99" />
                    {officeLocations.map((location, index) => <Marker key={index} position={location.position} data-id="element-100">
                        <Popup data-id="element-101">
                          <div data-id="element-102">
                            <strong data-id="element-103">{location.name}</strong>
                            <br data-id="element-104" />
                            {location.address}
                            <br data-id="element-105" />
                            Tel: {location.phone}
                          </div>
                        </Popup>
                      </Marker>)}
                  </MapContainer>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section - Bold & Direct */}
        <section id="contact" className="py-16 sm:py-20 lg:py-24 bg-gray-50 dark:bg-gray-900 relative" data-id="element-106">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-navy via-teal to-navy" data-id="element-107"></div>

          <div className="container mx-auto max-w-[var(--maxw)] px-4" data-id="element-108">
            <div className="text-center mb-10 sm:mb-16" data-id="element-109">
              <span className="text-teal font-bold tracking-widest uppercase text-xs sm:text-sm block mb-3 sm:mb-4" data-id="element-110">
                GET IN TOUCH
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy dark:text-white mb-4 sm:mb-6" data-id="element-111">
                Contact Us
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto" data-id="element-112">
                Get in touch with us for inquiries about our freight logistics,
                aviation technology, or consultancy services.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-10 sm:mb-16" data-id="element-113">
              <div className="bg-white dark:bg-gray-800 p-6 sm:p-8 lg:p-10 shadow-sm text-center group hover:-translate-y-2 transition-transform duration-300" data-id="element-114">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 text-teal" data-id="element-115">
                  <MailIcon className="w-6 h-6 sm:w-8 sm:h-8" data-id="element-116" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-navy dark:text-white mb-3 sm:mb-4" data-id="element-117">
                  Email
                </h3>
                <a href="mailto:contact@cosmoimageaviation.co" className="text-gray-600 dark:text-gray-300 hover:text-teal transition-colors break-all text-sm sm:text-base lg:text-lg" data-id="element-118">
                  contact@cosmoimageaviation.co
                </a>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 sm:p-8 lg:p-10 shadow-sm text-center group hover:-translate-y-2 transition-transform duration-300" data-id="element-119">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 text-teal" data-id="element-120">
                  <PhoneIcon className="w-6 h-6 sm:w-8 sm:h-8" data-id="element-121" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-navy dark:text-white mb-3 sm:mb-4" data-id="element-122">
                  Phone
                </h3>
                <a href="tel:+6581266622" className="text-gray-600 dark:text-gray-300 hover:text-teal transition-colors text-lg block mb-2" data-id="element-123">
                  +65 8126 6622
                </a>
                <p className="text-gray-500 text-sm" data-id="element-124">Fax: +65 6234 3152</p>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 sm:p-8 lg:p-10 shadow-sm text-center group hover:-translate-y-2 transition-transform duration-300 sm:col-span-2 md:col-span-1" data-id="element-125">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 text-teal" data-id="element-126">
                  <MapPinIcon className="w-6 h-6 sm:w-8 sm:h-8" data-id="element-127" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-navy dark:text-white mb-3 sm:mb-4" data-id="element-128">
                  Head Office
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base lg:text-lg" data-id="element-129">
                  1 Liang Seah Street, #02-02
                  <br data-id="element-130" />
                  Singapore 189022
                </p>
              </div>
            </div>

            <div className="bg-navy p-8 sm:p-12 md:p-16 text-center relative overflow-hidden" data-id="element-131">
              <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-132"></div>
              <div className="relative z-10" data-id="element-133">
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-6 sm:mb-8" data-id="element-134">
                  Ready to start your journey?
                </h3>
                <a href="mailto:contact@cosmoimageaviation.co" className="inline-block bg-teal hover:bg-white hover:text-navy text-white font-bold py-3 sm:py-4 px-8 sm:px-12 transition-all duration-300 shadow-lg text-sm sm:text-base" data-id="element-135">
                  Send Us an Email
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer data-id="element-136" />

        <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'COSMO IMAGE AVIATION PTE. LTD.',
          url: 'https://www.cosmoimageaviation.com/',
          logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cw3ea4867NqajmUxwmCcxJ/Logo_-_Company_-_HD.png',
          foundingDate: '2023-03-30',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '115 Airport Cargo Road, #01-01, Cargo Agents Building C',
            addressLocality: 'Singapore',
            postalCode: '819466',
            addressCountry: 'SG'
          },
          telephone: '+65 8126 6622',
          email: 'contact@cosmoimageaviation.co',
          sameAs: ['https://www.linkedin.com/company/cosmoimageaviation', 'https://www.facebook.com/cosmoimageaviation']
        })
      }} data-id="element-137" />
      </div>
    </div>;
}